import { useState, useEffect } from 'react';

/**
 * Custom hook to detect if the device is mobile
 * @param {number} breakpoint - Width threshold for mobile (default 768px)
 * @returns {boolean} - True if mobile
 */
export function useMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    window.addEventListener('resize', handleResize);
    // Initial check
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
}

/**
 * Hook for mobile responsive styles
 * Returns an object with conditional styles
 */
export function useMobileStyles() {
  const isMobile = useMobile();

  return {
    isMobile,
    containerPadding: isMobile ? 'var(--mobile-padding-screen, 16px)' : '1.5rem',
    cardPadding: isMobile ? 'var(--mobile-padding-card, 16px)' : '1.5rem',
    fontSize: {
      base: isMobile ? 'var(--mobile-font-base, 16px)' : '1rem',
      sm: isMobile ? 'var(--mobile-font-sm, 14px)' : '0.875rem',
      lg: isMobile ? 'var(--mobile-font-lg, 18px)' : '1.125rem',
      xl: isMobile ? 'var(--mobile-font-xl, 20px)' : '1.25rem',
      '2xl': isMobile ? 'var(--mobile-font-2xl, 24px)' : '1.5rem'
    },
    gap: {
      sm: isMobile ? 'var(--mobile-gap-sm, 8px)' : '0.5rem',
      md: isMobile ? 'var(--mobile-gap-md, 16px)' : '1rem',
      lg: isMobile ? 'var(--mobile-gap-lg, 24px)' : '1.5rem'
    },
    borderRadius: isMobile ? 'var(--mobile-radius-md, 12px)' : '0.75rem',
    touchTarget: isMobile ? 'var(--mobile-touch-min, 48px)' : 'auto'
  };
}

/**
 * Hook for responsive grid layout
 */
export function useResponsiveGrid() {
  const isMobile = useMobile();

  return {
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: isMobile ? 'var(--mobile-gap-md, 16px)' : '1.5rem'
  };
}

/**
 * Get mobile-friendly classes based on screen size
 */
export function getMobileClasses(isMobile) {
  return {
    hide: isMobile ? 'desktop-only' : '',
    show: isMobile ? 'mobile-only' : '',
    fullWidth: isMobile ? 'full-width' : ''
  };
}
